/**
 * "Attached printer" — the installed Windows print queues and COM ports a
 * desktop register can print to directly.
 *
 * Each row identifies its Windows queue port or serial port, whether Windows
 * reports it offline, and whether it is already saved. Choosing one leads to
 * the same check → test page → save flow as a network printer. Nothing is
 * saved by listing or checking.
 */
import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import MuiUsbIcon from '@mui/icons-material/Usb';
import Typography from '@mui/material/Typography';
import MuiCableIcon from '@mui/icons-material/Cable';
import MuiPrintIcon from '@mui/icons-material/Print';
import ListItemText from '@mui/material/ListItemText';
import MuiRefreshIcon from '@mui/icons-material/Refresh';
import ListItemButton from '@mui/material/ListItemButton';
import LinearProgress from '@mui/material/LinearProgress';
import MuiChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { EscPosSerialPort, EscPosWindowsQueue } from 'src/platform/services';
import { escPosQueueIdentifier, escPosSerialIdentifier } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import type { EscPosDevicesState } from './use-escpos-printer-setup';

// ----------------------------------------------------------------------

interface EscPosAttachedPanelProps {
  devices: EscPosDevicesState;
  savedIds: ReadonlySet<string>;
  currentDefaultId?: string | null;
  onRefresh: () => void;
  onSelectQueue: (queue: EscPosWindowsQueue) => void;
  onSelectSerialPort: (port: EscPosSerialPort) => void;
}

function queueOffline(queue: EscPosWindowsQueue): boolean {
  return queue.workOffline || queue.printerStatus === 7 || queue.detectedErrorState === 9;
}

function usbLabel(usb: { vendorId: string; productId: string; serialNumber?: string } | undefined): string | null {
  if (!usb) return null;
  return `USB ${usb.vendorId}:${usb.productId}${usb.serialNumber ? ` · S/N ${usb.serialNumber}` : ''}`;
}

export default function EscPosAttachedPanel({
  devices,
  savedIds,
  currentDefaultId,
  onRefresh,
  onSelectQueue,
  onSelectSerialPort,
}: EscPosAttachedPanelProps) {
  // List once when the panel appears; further refreshes are the operator's.
  useEffect(() => {
    if (devices.phase === 'idle') onRefresh();
  }, [devices.phase, onRefresh]);

  const {result} = devices;
  const loading = devices.phase === 'loading';
  const printers = result?.printers ?? [];
  const ports = result?.serialPorts ?? [];

  return (
    <Stack spacing={2} sx={{ px: 3, pb: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Printers installed on this register. Receipt bytes go directly to the selected Windows queue
          or serial port. Check that the queue’s port matches the connected printer.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={loading}
          onClick={onRefresh}
          startIcon={<MuiIcon icon={MuiRefreshIcon} />}
          sx={{ flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Stack>

      {loading && <LinearProgress />}

      {devices.phase === 'failed' && (
        <Alert severity="error" action={<Button onClick={onRefresh}>Retry</Button>}>
          {devices.error}
        </Alert>
      )}

      {result && !result.supported && (
        <Alert severity="info">{result.reason ?? 'Attached printers are not available on this device.'}</Alert>
      )}

      {result?.supported && printers.length === 0 && ports.length === 0 && !loading && (
        <Stack alignItems="center" spacing={1} sx={{ py: 3, textAlign: 'center' }}>
          <MuiIcon icon={MuiPrintIcon} width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="subtitle1">No attached printers found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
            Install the printer in Windows first (its own driver or “Generic / Text Only”), plug it in and
            switch it on, then refresh. Printers on the network are added under Network.
          </Typography>
        </Stack>
      )}

      {printers.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Windows print queues
          </Typography>
          <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            {printers.map((queue) => {
              const id = escPosQueueIdentifier(queue.name);
              const offline = queueOffline(queue);
              return (
                <ListItem key={id} disablePadding divider>
                  <ListItemButton onClick={() => onSelectQueue(queue)} sx={{ py: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', mr: 2 }}>
                      <MuiIcon icon={queue.kind === 'usb' ? MuiUsbIcon : MuiPrintIcon} width={22} />
                    </Avatar>
                    <ListItemText
                      disableTypography
                      primary={<Typography variant="subtitle2">{queue.name}</Typography>}
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {queue.driverName} · {queue.portName}
                            {usbLabel(queue.usb) ? ` · ${usbLabel(queue.usb)}` : ''}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip
                              label={{ usb: 'USB printer', 'serial-queue': 'Serial port queue', parallel: 'Parallel port queue', other: 'Windows print queue' }[queue.kind]}
                              size="small"
                              color="primary"
                              variant="soft"
                            />
                            {currentDefaultId === id && <Chip label="Active" size="small" color="primary" variant="soft" />}
                            {savedIds.has(id) && currentDefaultId !== id && (
                              <Chip label="Saved" size="small" variant="soft" />
                            )}
                            {offline && <Chip label="Offline (Windows)" size="small" color="warning" variant="soft" />}
                            {queue.isDefault && <Chip label="Windows default" size="small" variant="outlined" />}
                          </Stack>
                        </Stack>
                      }
                    />
                    <MuiIcon icon={MuiChevronRightIcon} sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {ports.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Serial ports
          </Typography>
          <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            {ports.map((port) => {
              const id = escPosSerialIdentifier(port.portName);
              return (
                <ListItem key={id} disablePadding divider>
                  <ListItemButton onClick={() => onSelectSerialPort(port)} sx={{ py: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'info.lighter', color: 'info.main', mr: 2 }}>
                      <MuiIcon icon={MuiCableIcon} width={22} />
                    </Avatar>
                    <ListItemText
                      disableTypography
                      primary={<Typography variant="subtitle2">{port.portName}</Typography>}
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {port.friendlyName ?? 'Serial port'}
                            {usbLabel(port.usb) ? ` · ${usbLabel(port.usb)}` : ''}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip label="Serial printer" size="small" color="info" variant="soft" />
                            {currentDefaultId === id && <Chip label="Active" size="small" color="primary" variant="soft" />}
                            {savedIds.has(id) && currentDefaultId !== id && (
                              <Chip label="Saved" size="small" variant="soft" />
                            )}
                          </Stack>
                        </Stack>
                      }
                    />
                    <MuiIcon icon={MuiChevronRightIcon} sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Only choose a port that belongs to a receipt printer; other serial devices may be listed too.
          </Typography>
        </Box>
      )}

      {result && result.notes.length > 0 && <Alert severity="info">{result.notes.join(' ')}</Alert>}
    </Stack>
  );
}
